import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { adminApi } from '../services/api';
import {
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  GlobeAltIcon,
  TicketIcon,
  DocumentTextIcon,
  CreditCardIcon,
  BuildingOffice2Icon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  GiftIcon,
  EnvelopeIcon,
  InboxIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Users', href: '/users', icon: UsersIcon },
  { name: 'Companies', href: '/companies', icon: BuildingOfficeIcon },
  { name: 'Subscription Plans', href: '/subscription-plans', icon: CreditCardIcon },
  { name: 'Invoices', href: '/invoices', icon: DocumentDuplicateIcon },
  { name: 'Mailbox', href: '/mailbox/sales', icon: InboxIcon },
  { name: 'Enterprise Inquiries', href: '/enterprise-inquiries', icon: BuildingOffice2Icon },
  { name: 'Referrals', href: '/referrals', icon: GiftIcon },
  { name: 'Invite Codes', href: '/invite-codes', icon: TicketIcon },
  { name: 'Regions', href: '/regions', icon: GlobeAltIcon },
  { name: 'Cache', href: '/cache', icon: CircleStackIcon },
  { name: 'Issues', href: '/issues', icon: ExclamationTriangleIcon },
  { name: 'Support Tickets', href: '/tickets', icon: TicketIcon },
  { name: 'Documentation', href: '/documentation', icon: DocumentTextIcon },
  { name: 'AI Project Manager', href: '/ai-pm', icon: SparklesIcon },
  { name: 'System Settings', href: '/settings', icon: Cog6ToothIcon },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openIssues, setOpenIssues] = useState(0);
  const [notClosedTickets, setNotClosedTickets] = useState(0);
  const [newEnterpriseInquiries, setNewEnterpriseInquiries] = useState(0);
  const [unreadSalesContacts, setUnreadSalesContacts] = useState(0);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  
  // Poll for open issues, not-closed ticket counts, enterprise inquiries, and mailbox contacts
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [issuesResponse, ticketsResponse, enterpriseResponse, salesResponse, supportResponse] = await Promise.all([
          adminApi.getOpenIssueCount(),
          adminApi.getNotClosedTicketCount(),
          adminApi.getEnterpriseInquiryCounts(),
          adminApi.getMailboxUnreadCount('sales').catch(() => ({ data: { count: 0 } })),
          adminApi.getMailboxUnreadCount('support').catch(() => ({ data: { count: 0 } }))
        ]);
        setOpenIssues(issuesResponse.data.count || 0);
        setNotClosedTickets(ticketsResponse.data.count || 0);
        setNewEnterpriseInquiries(enterpriseResponse.data.new || 0);
        // Combine sales and support unread counts for the sidebar badge
        const totalUnread = (salesResponse.data.count || 0) + (supportResponse.data.count || 0);
        setUnreadSalesContacts(totalUnread);
      } catch (error) {
        // Silently fail
      }
    };
    
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000); // Poll every 30 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  return (
    <div className="min-h-screen bg-surface-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-surface-950 border-r border-surface-800
        transform transition-transform duration-200 ease-in-out flex flex-col
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-surface-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="w-8 h-8 text-primary-500" />
            <span className="text-xl font-bold text-surface-100">Neoshift</span>
            <span className="text-xs bg-primary-500/20 text-primary-400 px-2 py-0.5 rounded">Admin</span>
          </div>
          <button 
            className="lg:hidden text-surface-400 hover:text-surface-100"
            onClick={() => setSidebarOpen(false)}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) => {
                // Special case: Mailbox should be active for any /mailbox/* path
                const isMailboxActive = item.href.startsWith('/mailbox/') && 
                  window.location.hash.includes('/mailbox');
                return (isActive || isMailboxActive) ? 'sidebar-link-active' : 'sidebar-link';
              }}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="w-5 h-5" />
              {item.name}
              {item.href === '/issues' && openIssues > 0 && (
                <span className="ml-auto bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {openIssues > 99 ? '99+' : openIssues}
                </span>
              )}
              {item.href === '/tickets' && notClosedTickets > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {notClosedTickets > 99 ? '99+' : notClosedTickets}
                </span>
              )}
              {item.href === '/enterprise-inquiries' && newEnterpriseInquiries > 0 && (
                <span className="ml-auto bg-primary-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {newEnterpriseInquiries > 99 ? '99+' : newEnterpriseInquiries}
                </span>
              )}
              {item.href === '/mailbox/sales' && unreadSalesContacts > 0 && (
                <span className="ml-auto bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {unreadSalesContacts > 99 ? '99+' : unreadSalesContacts}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        
        {/* User section */}
        <div className="p-4 border-t border-surface-800 flex-shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
              <span className="text-primary-400 font-medium">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-surface-100 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-surface-400 truncate">
                {user?.role?.replace('_', ' ')}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full btn-ghost text-sm justify-start"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            Sign out
          </button>
        </div>
      </aside>
      
      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="h-16 bg-surface-900 border-b border-surface-800 flex items-center px-4 sticky top-0 z-30">
          <button
            className="lg:hidden text-surface-400 hover:text-surface-100 mr-4"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          <div className="flex-1" />
          <div className="text-sm text-surface-400">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </header>
        
        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

